import type { ResultOf } from '@graphql-typed-document-node/core'
import type { QueryObserverResult } from '@tanstack/query-core'
import { atom, useAtomValue, useSetAtom } from 'jotai'
import type { Atom } from 'jotai'
import { atomEffect } from 'jotai-effect'
import { atomWithQuery } from 'jotai-tanstack-query'
import { useCallback } from 'react'
import { RedirectError } from '~/features/reservationInput/store/lib/redirectError'
import { CancelPolicyPolicies } from '~/features/reservationInput/store/stateMachine/cancelPolicyPolicies.frag'
import { MemberCards } from '~/features/reservationInput/store/stateMachine/memberCards.frag'
import { PlanCancelPolicy } from '~/features/reservationInput/store/stateMachine/planCancelPolicy.frag'
import { ClientError } from '~/lib/error/clientError'
import type { FragmentType } from '~/lib/graphql'
import { getFragmentData } from '~/lib/graphql'
import type { ReserveInputContext } from '~/navigation'
import { env$ } from '~/stores/env'
import { createQueryAtom, createQueryOptions } from '~/stores/jotaiQueryHelper'
import { deletedCardIds$ } from '../card'
import { atomWithError } from '../error'
import { reservationInputState$, reservationInputStateValue$, userType$ } from './context'
import { GetReserveInput } from './getReserveInput.graphql'
import { InventorySeats } from './inventorySeats.frag'
import { PlanReserveInfo } from './planReserveInfo.frag'
import { PlanRestaurant } from './planRestaurant.frag'
import { ReserveInfoChildMenus } from './reserveInfoChildMenus.frag'
import { ReserveInfoMember } from './reserveInfoMember.frag'
import { ReserveInfoOptionMenus } from './reserveInfoOptionMenus.frag'
import { ReserveInfoPlan } from './reserveInfoPlan.frag'
import { ReserveInfoViewer } from './reserveInfoViewer.frag'
import type { ReservationInputEvent, ReservationInputState, UserType } from './statemachine'
import { reservationInputMachine } from './statemachine'

export const transition$ = atom(null, (get, set, event: ReservationInputEvent) => {
  const current = get(reservationInputState$)
  const next = reservationInputMachine.transition(current, event)
  const env = get(env$)
  if (!env.APP_ENV?.startsWith('production')) {
    if (next.changed) {
      // biome-ignore lint/suspicious/noConsole:
      console.info(
        `[reserve-input] transitioned by : ${event.type}  (${current.value} -> ${next.value})`,
        {
          event,
          current,
          next,
        }
      )
    } else {
      // biome-ignore lint/suspicious/noConsole:
      console.info(`[reserve-input] no transition occurred by: ${event.type} (${current.value})`, {
        event,
        current,
      })
    }
  }
  // current state に未定義の event が渡ってきたとき、稀に calendarState$が壊れて無限ループに
  // 陥ることがあるので、next.changed で正しい状態遷移か調べる
  if (next.changed) {
    set(reservationInputState$, next)
  }
  // 状態が変わったかどうかを返す
  return next.changed ?? false
})
transition$.debugLabel = 'transition$'

/** 予約入力の状態遷移イベントを実行し、状態が変わったかどうかを返す */
export function useTransition() {
  const send = useSetAtom(transition$)
  return useCallback(
    function (event: ReservationInputEvent) {
      return send(event)
    },
    [send]
  )
}

// INITIAL か RESERVE_INFO_LOADING の場合に atomEffect で watch している値を初期化させないためのフラグ
export const isInitialDisplay$ = atom((get) => {
  const value = get(reservationInputStateValue$)
  return value === 'INITIAL' || value === 'RESERVE_INFO_LOADING'
})
isInitialDisplay$.debugLabel = 'isInitialDisplay$'

const reserveInfoQueryVariables$ = atom((get) => {
  const state = get(reservationInputState$)
  return {
    visitDate: state.context?.visitDate,
    visitTime: state.context?.visitTime,
    visitorsCount: state.context?.visitorsCount,
    restaurantId: state.context?.restaurantId,
    planId: state.context?.planId,
  }
})
reserveInfoQueryVariables$.debugLabel = 'reserveInfoQueryVariables$'

export const navigationContext$ = atom<undefined | ReserveInputContext>(undefined)

export const reserveInfoQueryFn$ = createQueryAtom<typeof GetReserveInput>()
reserveInfoQueryFn$.debugLabel = 'reserveInfoQueryFn$'

const reserveInfoQuery$ = atomWithQuery((get) => {
  const state = get(reservationInputStateValue$)
  const reserveInfoQueryFn = get(reserveInfoQueryFn$).fn
  // gcTime を 0 に設定することで、前回のデータがキャッシュされないようにする
  // 2 回目以降の fetch でも state が loading になることは確認済み
  return createQueryOptions(GetReserveInput, get(reserveInfoQueryVariables$), reserveInfoQueryFn, {
    gcTime: 0,
    staleTime: 0,
    enabled: state === 'RESERVE_INFO_LOADING',
  })
})
reserveInfoQuery$.debugLabel = 'reserveInfoQuery$'

export const reserveInfoQueryResult$ = atom((get) => {
  return get(reserveInfoQuery$)
})
reserveInfoQueryResult$.debugLabel = 'reserveInfoQueryResult$'

const isLoading$ = atom((get) => {
  return isLoading(get(reserveInfoQueryResult$), get(reservationInputStateValue$))
})
isLoading$.debugLabel = 'isLoading$'

export function useIsLoading() {
  return useAtomValue(isLoading$)
}

function isLoading(query: QueryObserverResult, state: ReservationInputState['value']) {
  // query のみで判定すると、state が RESERVE_INFO_LOADING の遷移後にも isLoading が true になってしまい
  // reserveInfo$ を参照しようとしてエラーになるため、state も判定する
  return (
    query.status === 'pending' ||
    query.fetchStatus === 'fetching' ||
    state === 'RESERVE_INFO_LOADING'
  )
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('isLoading', () => {
    expect(
      isLoading({ status: 'success', fetchStatus: 'idle' } as QueryObserverResult, 'INITIAL')
    ).toBe(false)
    expect(
      isLoading({ status: 'pending', fetchStatus: 'idle' } as QueryObserverResult, 'INITIAL')
    ).toBe(true)
    expect(
      isLoading({ status: 'success', fetchStatus: 'fetching' } as QueryObserverResult, 'INITIAL')
    ).toBe(true)
    expect(
      isLoading(
        { status: 'success', fetchStatus: 'idle' } as QueryObserverResult,
        'RESERVE_INFO_LOADING'
      )
    ).toBe(true)
  })
}

export const isMember$ = atom((get) => {
  return isMember(get(userType$))
})
isMember$.debugLabel = 'isMember$'
function isMember(type?: UserType) {
  return type === 'MEMBER'
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('isMember', () => {
    expect(isMember(undefined)).toBe(false)
    expect(isMember('MEMBER')).toBe(true)
    expect(isMember('GUEST')).toBe(false)
    expect(isMember('PRE_MEMBER')).toBe(false)
  })
}
export function useIsMember() {
  return useAtomValue(isMember$)
}

export const isGuest$ = atom((get) => {
  return isGuest(get(userType$))
})
isGuest$.debugLabel = 'isGuest$'
function isGuest(type?: UserType) {
  return type === 'GUEST'
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('isGuest', () => {
    expect(isGuest(undefined)).toBe(false)
    expect(isGuest('MEMBER')).toBe(false)
    expect(isGuest('GUEST')).toBe(true)
    expect(isGuest('PRE_MEMBER')).toBe(false)
  })
}
export function useIsGuest() {
  return useAtomValue(isGuest$)
}

export const isPreMember$ = atom((get) => {
  return isPreMember(get(userType$))
})
isPreMember$.debugLabel = 'isPreMember$'
function isPreMember(type?: UserType) {
  return type === 'PRE_MEMBER'
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('isPreMember', () => {
    expect(isPreMember(undefined)).toBe(false)
    expect(isPreMember('MEMBER')).toBe(false)
    expect(isPreMember('GUEST')).toBe(false)
    expect(isPreMember('PRE_MEMBER')).toBe(true)
  })
}
export function useIsPreMember() {
  return useAtomValue(isPreMember$)
}

export const isMemberOrPreMember$ = atom((get) => {
  return isMemberOrPreMember(get(userType$))
})
isMemberOrPreMember$.debugLabel = 'isMemberOrPreMember$'
function isMemberOrPreMember(type?: UserType) {
  return type === 'MEMBER' || type === 'PRE_MEMBER'
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('isMemberOrPreMember', () => {
    expect(isMemberOrPreMember(undefined)).toBe(false)
    expect(isMemberOrPreMember('MEMBER')).toBe(true)
    expect(isMemberOrPreMember('GUEST')).toBe(false)
    expect(isMemberOrPreMember('PRE_MEMBER')).toBe(true)
  })
}
export function useIsMemberOrPreMember() {
  return useAtomValue(isMemberOrPreMember$)
}

export const isGuestOrPreMember$ = atom((get) => {
  return isGuestOrPreMember(get(userType$))
})
isGuestOrPreMember$.debugLabel = 'isGuestOrPreMember$'
function isGuestOrPreMember(type?: UserType) {
  return type === 'GUEST' || type === 'PRE_MEMBER'
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('isGuestOrPreMember', () => {
    expect(isMemberOrPreMember(undefined)).toBe(false)
    expect(isGuestOrPreMember('MEMBER')).toBe(false)
    expect(isGuestOrPreMember('GUEST')).toBe(true)
    expect(isGuestOrPreMember('PRE_MEMBER')).toBe(true)
  })
}

export const reserveInfo$ = atomWithError<ReserveInputReserveInfo | undefined>((get) => {
  const query = get(reserveInfoQueryResult$)
  if (query.status !== 'success') {
    return undefined
  }
  return getReserveInfo(query.data)
})
reserveInfo$.debugLabel = 'reserveInfo$'

const restaurantInfo$ = atom((get) => {
  const query = get(reserveInfoQueryResult$)
  if (query.status !== 'success') {
    return undefined
  }
  return query.data.restaurant
})

export function useRestaurantInfo() {
  return useAtomValue(restaurantInfo$)
}

// ../specification.md の「カスタムフックの使用条件について」参照
export function useReserveInfo() {
  return useAtomValue(reserveInfo$)
}

export type ReserveInputReserveInfo = ReturnType<typeof getReserveInfo>
export function getReserveInfo(query: ResultOf<typeof GetReserveInput>) {
  if (query.restaurant?.plan?.reserveInfo.__typename === 'ReserveInfo') {
    return getFragmentData(PlanReserveInfo, query.restaurant?.plan?.reserveInfo)
  } else if (query.restaurant?.plan?.reserveInfo.__typename === 'ReserveInfoError') {
    throw ClientError.fromHtmlFragments(query.restaurant.plan.reserveInfo.messages)
  }
  // Yahoo! 連携の refresh token が期限切れのときは、宿泊に合わせてログイン画面にリダイレクトさせる
  else if (query.restaurant?.plan?.reserveInfo.__typename === 'ReserveInfoRedirect') {
    throw RedirectError.withoutUrl(query.restaurant.plan.reserveInfo.redirect)
  }
  throw new Error('ReserveInfo is not found')
}

export const plan$: Atom<ReserveInputPlan | undefined> = atom((get) => {
  const reserveInfo = get(reserveInfo$)
  return reserveInfo ? getPlan(reserveInfo) : undefined
})
plan$.debugLabel = 'plan$'

// ../specification.md の「カスタムフックの使用条件について」参照
export function usePlan() {
  return useAtomValue(plan$)
}

const isRequestReservation$ = atom((get) => {
  return get(plan$)?.isRequestReservation ?? false
})
isRequestReservation$.debugLabel = 'isRequestReservation$'

export function useIsRequestReservation() {
  return useAtomValue(isRequestReservation$)
}

export type ReserveInputPlan = ReturnType<typeof getPlan>
function getPlan(reserveInfo: FragmentType<typeof ReserveInfoPlan>) {
  return getFragmentData(ReserveInfoPlan, reserveInfo).plan
}

export const seats$: Atom<ReserveInputSeats | []> = atom((get) => {
  const reserveInfo = get(reserveInfo$)
  return reserveInfo ? getSeats(reserveInfo.inventory) : []
})
seats$.debugLabel = 'seats$'

export type ReserveInputSeats = ReturnType<typeof getSeats>
export type ReserveInputSeat = ReserveInputSeats[number]
function getSeats(reserveInfo: FragmentType<typeof InventorySeats>) {
  return getFragmentData(InventorySeats, reserveInfo).seats
}

export const childMenus$: Atom<ReserveInputChildMenus | []> = atom((get) => {
  const reserveInfo = get(reserveInfo$)
  return reserveInfo ? getChildMenus(reserveInfo) : []
})
childMenus$.debugLabel = 'childMenus$'

export type ReserveInputChildMenus = ReturnType<typeof getChildMenus>
export type ReserveInputChildMenu = ReserveInputChildMenus[number]
function getChildMenus(reserveInfo: FragmentType<typeof ReserveInfoChildMenus>) {
  return getFragmentData(ReserveInfoChildMenus, reserveInfo).childMenus
}

// ../specification.md の「カスタムフックの使用条件について」参照
export function useChildMenus() {
  return useAtomValue(childMenus$)
}

export const optionMenus$: Atom<ReserveInputOptionMenus | []> = atom((get) => {
  const reserveInfo = get(reserveInfo$)
  return reserveInfo ? getOptionMenus(reserveInfo) : []
})
optionMenus$.debugLabel = 'optionMenus$'

export type ReserveInputOptionMenus = ReturnType<typeof getOptionMenus>
export type ReserveInputOptionMenu = ReserveInputOptionMenus[number]
function getOptionMenus(reserveInfo: FragmentType<typeof ReserveInfoOptionMenus>) {
  return getFragmentData(ReserveInfoOptionMenus, reserveInfo).optionMenus
}

// ../specification.md の「カスタムフックの使用条件について」参照
export function useOptionMenus() {
  return useAtomValue(optionMenus$)
}

export const restaurant$: Atom<ReserveInputRestaurant | undefined> = atom((get) => {
  const plan = get(plan$)
  return plan ? getRestaurant(plan) : undefined
})
restaurant$.debugLabel = 'restaurant$'

// ../specification.md の「カスタムフックの使用条件について」参照
export function useRestaurant() {
  return useAtomValue(restaurant$)
}

export type ReserveInputRestaurant = ReturnType<typeof getRestaurant>
function getRestaurant(plan: FragmentType<typeof PlanRestaurant>) {
  return getFragmentData(PlanRestaurant, plan).restaurant
}

export const cancelPolicy$: Atom<ReserveInputCancelPolicy | undefined> = atom((get) => {
  const plan = get(plan$)
  return plan ? getCancelPolicy(plan) : undefined
})
cancelPolicy$.debugLabel = 'cancelPolicy$'

// ../specification.md の「カスタムフックの使用条件について」参照
export function useCancelPolicy() {
  return useAtomValue(cancelPolicy$)
}

export type ReserveInputCancelPolicy = ReturnType<typeof getCancelPolicy>
function getCancelPolicy(plan: FragmentType<typeof PlanCancelPolicy>) {
  const cancelPolicy = getFragmentData(PlanCancelPolicy, plan).cancelPolicy
  if (cancelPolicy === null) {
    return undefined
  }
  return cancelPolicy
}

export const policies$: Atom<ReserveInputPolicies | []> = atom((get) => {
  const cancelPolicy = get(cancelPolicy$)
  if (cancelPolicy?.__typename === 'CancelPolicyText') {
    return []
  }
  return cancelPolicy ? getPolicies(cancelPolicy) : []
})
policies$.debugLabel = 'policies$'

export type ReserveInputPolicies = ReturnType<typeof getPolicies>
export type ReserveInputPolicy = ReserveInputPolicies[number]
function getPolicies(cancelPolicy: FragmentType<typeof CancelPolicyPolicies>) {
  return getFragmentData(CancelPolicyPolicies, cancelPolicy).policies
}

export const viewer$: Atom<ReserveInputViewer | undefined> = atom((get) => {
  const query = get(reserveInfoQueryResult$)
  if (query.status !== 'success') {
    return undefined
  }
  return getViewer(query.data)
})

// ../specification.md の「カスタムフックの使用条件について」参照
export function useViewer() {
  return useAtomValue(viewer$)
}

export type ReserveInputViewer = ReturnType<typeof getViewer>
function getViewer(query: ResultOf<typeof GetReserveInput>) {
  return query.viewer ? getFragmentData(ReserveInfoViewer, query.viewer) : undefined
}

// ../specification.md の「カスタムフックの使用条件について」参照
export const member$: Atom<ReserveInputMember | undefined> = atom((get) => {
  const reserveInfo = get(reserveInfo$)
  return reserveInfo ? getMember(reserveInfo) : undefined
})
member$.debugLabel = 'member$'

export type ReserveInputMember = ReturnType<typeof getMember>
export function getMember(reserveInfo: FragmentType<typeof ReserveInfoMember>) {
  const member = getFragmentData(ReserveInfoMember, reserveInfo).member
  if (!member) {
    return undefined
  }
  return member
}

export const cards$: Atom<ReserveInputCards> = atom((get) => {
  const member = get(member$)
  const deletedCardIds = get(deletedCardIds$)
  return member ? getCards(member, deletedCardIds) : []
})
cards$.debugLabel = 'cards$'

// ../specification.md の「カスタムフックの使用条件について」参照
export function useCards() {
  return useAtomValue(cards$)
}

export type ReserveInputCards = ReturnType<typeof getCards>
export type ReserveInputCard = ReserveInputCards[number]
function getCards(member: FragmentType<typeof MemberCards>, deletedCardIds: string[]) {
  const cards = getFragmentData(MemberCards, member).cards
  // 削除済カードを除外
  const activeCards = cards.filter((card) => !deletedCardIds.includes(card.cardId))
  // isPrimary なカードがある場合は先頭に持ってくる
  const primary = activeCards.find((card) => card.isPrimary)
  if (primary) {
    return [primary, ...activeCards.filter((card) => !card.isPrimary)]
  }
  return activeCards ?? []
}

// ../specification.md 「atomEffect について」参照
export const listenInitEvent$ = atomEffect((get, set) => {
  // TODO: state value の監視は本当に必要か確認する
  // 「ブラウザで戻って進んで結局同じプランで何も変わらずに予約入力まで画面遷移したときに
  // restaurantId と query は値が変わらないので atomEffect が実行されずに init されない」
  // という状況が起こりえるため、state value を監視している
  const state = get(reservationInputStateValue$)
  if (state !== 'INITIAL') {
    return
  }

  const navigationContext = get(navigationContext$)
  if (navigationContext === undefined) {
    return
  }
  set.recurse(transition$, {
    type: 'init',
    ...navigationContext,
    givenPointsUsage: 'SAVE',
    givenPointsType: 'IKYU',
    isDiscountCouponEnabled: false,
    ikyuPointUsage: 0,
    isIkyuPointEnabled: false,
    isAllIkyuPointSelected: false,
    paypayPointUsage: 0,
    isPayPayPointEnabled: false,
    isAllPayPayPointSelected: false,
    childCount: 0,
    isSameReserverAsVisitor: true,
    affiliateSiteFormAnswer: '',
    surveyAnswer: '',
    agreedToReserveRequest: false,
  })
})
listenInitEvent$.debugLabel = 'listenInitEvent$'
